import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { Button, Input, Card, CardContent } from "@/shared/components/ui";
import {
  UserPlus,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  User,
  Briefcase,
  Users,
  Building,
  Wallet,
} from "lucide-react";

// Types for form data
type AdvisorRoleType = "doradca" | "ekspert" | "oba";
type RPKDivision = "I" | "II" | "I_II";
type CompensationModel = "client" | "bank" | "mixed";

interface FormData {
  // Step 1: Role selection
  roleType: AdvisorRoleType | "";
  // Step 2: Basic data
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  company: string;
  phone: string;
  // Step 3: Regulatory data (conditional)
  rpkNumber: string;
  rpkDivision: RPKDivision | "";
  nip: string;
  // Step 4: Compensation model
  compensationModel: CompensationModel | "";
}

const INITIAL_FORM: FormData = {
  roleType: "",
  email: "",
  password: "",
  confirmPassword: "",
  name: "",
  company: "",
  phone: "",
  rpkNumber: "",
  rpkDivision: "",
  nip: "",
  compensationModel: "",
};

// Step indicator component
function StepIndicator({
  currentStep,
  totalSteps,
  stepLabels,
}: {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}) {
  return (
    <div className="flex items-center justify-center mb-8">
      {stepLabels.slice(0, totalSteps).map((label, index) => (
        <div key={index} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index + 1 < currentStep
                  ? "bg-primary-600 text-white"
                  : index + 1 === currentStep
                    ? "bg-primary-600 text-white ring-4 ring-primary-100"
                    : "bg-gray-200 text-gray-500"
              }`}
            >
              {index + 1 < currentStep ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                index + 1
              )}
            </div>
            <span
              className={`text-xs mt-1 ${
                index + 1 <= currentStep ? "text-primary-600" : "text-gray-400"
              }`}
            >
              {label}
            </span>
          </div>
          {index < totalSteps - 1 && (
            <div
              className={`w-12 h-0.5 mx-2 ${
                index + 1 < currentStep ? "bg-primary-600" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// Role selection card component
function RoleCard({
  selected,
  onClick,
  icon: Icon,
  title,
  description,
}: {
  selected: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
        selected
          ? "border-primary-600 bg-primary-50"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`p-2 rounded-lg ${
            selected ? "bg-primary-100" : "bg-gray-100"
          }`}
        >
          <Icon
            className={`w-5 h-5 ${
              selected ? "text-primary-600" : "text-gray-500"
            }`}
          />
        </div>
        <div>
          <h3
            className={`font-medium ${
              selected ? "text-primary-900" : "text-gray-900"
            }`}
          >
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}

export function RegisterPage() {
  const { register, isLoading, error, clearError } = useAdvisorStore();
  const [form, setForm] = useState<FormData>(INITIAL_FORM);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Determine if RPK step is needed
  const needsRpkData = form.roleType === "ekspert" || form.roleType === "oba";
  const totalSteps = needsRpkData ? 4 : 3;

  const stepLabels = needsRpkData
    ? ["Rola", "Dane", "RPK", "Wynagrodzenie"]
    : ["Rola", "Dane", "Wynagrodzenie"];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setValidationError("");
    clearError();
  };

  const handleRoleSelect = (role: AdvisorRoleType) => {
    setForm((prev) => ({
      ...prev,
      roleType: role,
      // Set default compensation model based on role
      compensationModel:
        role === "doradca" ? "client" : role === "ekspert" ? "bank" : "",
    }));
    setValidationError("");
  };

  const validateStep = (): boolean => {
    switch (step) {
      case 1: // Role selection
        if (!form.roleType) {
          setValidationError("Wybierz swoją rolę");
          return false;
        }
        return true;

      case 2: // Basic data
        if (!form.name || form.name.length < 2) {
          setValidationError("Imię i nazwisko jest wymagane");
          return false;
        }
        if (!form.email || !form.email.includes("@")) {
          setValidationError("Podaj prawidłowy adres email");
          return false;
        }
        if (form.password.length < 8) {
          setValidationError("Hasło musi mieć co najmniej 8 znaków");
          return false;
        }
        if (form.password !== form.confirmPassword) {
          setValidationError("Hasła nie są takie same");
          return false;
        }
        return true;

      case 3: // RPK data (only if needsRpkData)
        if (needsRpkData) {
          if (!form.rpkNumber) {
            setValidationError("Numer wpisu RPKIP jest wymagany");
            return false;
          }
          if (!form.rpkDivision) {
            setValidationError("Wybierz dział rejestru RPKIP");
            return false;
          }
        }
        return true;

      case 4: // Compensation model
        if (!form.compensationModel) {
          setValidationError("Wybierz model wynagrodzenia");
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    setValidationError("");
    clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationError("");

    if (!validateStep()) {
      return;
    }

    try {
      const result = await register({
        email: form.email,
        password: form.password,
        name: form.name,
        company: form.company || undefined,
        phone: form.phone || undefined,
        roleType: form.roleType || undefined,
        rpkNumber: form.rpkNumber || undefined,
        rpkDivision: form.rpkDivision || undefined,
        nip: form.nip || undefined,
        compensationModel: form.compensationModel || undefined,
      });

      if (result.success) {
        setSuccess(true);
      }
    } catch {
      // Error is handled by the store
    }
  };

  // Determine which step is the last one based on whether RPK is needed
  const isLastStep =
    (needsRpkData && step === 4) || (!needsRpkData && step === 3);
  const isCompensationStep =
    (needsRpkData && step === 4) || (!needsRpkData && step === 3);

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-success-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Rejestracja zakończona!
              </h2>
              <p className="text-gray-600 mb-4">
                Twoje konto zostało utworzone. Administrator musi je
                zweryfikować przed pierwszym logowaniem.
              </p>
              {needsRpkData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-700">
                    <strong>Uwaga:</strong> Jako pośrednik kredytowy, Twój wpis
                    RPKIP zostanie zweryfikowany przez nasz zespół w rejestrze
                    KNF.
                  </p>
                </div>
              )}
              <Link to="/login">
                <Button className="w-full">Przejdź do logowania</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Rejestracja</h1>
          <p className="text-gray-600 mt-2">Utwórz konto w panelu doradcy</p>
        </div>

        <Card>
          <CardContent className="p-6">
            <StepIndicator
              currentStep={step}
              totalSteps={totalSteps}
              stepLabels={stepLabels}
            />

            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || validationError) && (
                <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error-700">
                    {error || validationError}
                  </p>
                </div>
              )}

              {/* Step 1: Role Selection */}
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Wybierz swoją rolę
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Określ, w jakim charakterze działasz na rynku finansowym
                  </p>

                  <div className="space-y-3">
                    <RoleCard
                      selected={form.roleType === "doradca"}
                      onClick={() => handleRoleSelect("doradca")}
                      icon={User}
                      title="Doradca Finansowy"
                      description="Niezależny doradca płatny przez klienta. Działa w interesie klienta, bez powiązań z bankami."
                    />

                    <RoleCard
                      selected={form.roleType === "ekspert"}
                      onClick={() => handleRoleSelect("ekspert")}
                      icon={Briefcase}
                      title="Ekspert Finansowy (Pośrednik)"
                      description="Pośrednik kredytowy z wpisem RPKIP. Otrzymuje prowizję od banków za pozyskanie klientów."
                    />

                    <RoleCard
                      selected={form.roleType === "oba"}
                      onClick={() => handleRoleSelect("oba")}
                      icon={Users}
                      title="Oba modele"
                      description="Działasz zarówno jako niezależny doradca, jak i pośrednik kredytowy z wpisem RPKIP."
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Basic Data */}
              {step === 2 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Dane podstawowe
                  </h2>

                  <Input
                    label="Imię i nazwisko"
                    name="name"
                    placeholder="Jan Kowalski"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />

                  <Input
                    label="Email"
                    type="email"
                    name="email"
                    placeholder="jan.kowalski@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                  />

                  <Input
                    label="Telefon (opcjonalnie)"
                    type="tel"
                    name="phone"
                    placeholder="+48 123 456 789"
                    value={form.phone}
                    onChange={handleChange}
                  />

                  <Input
                    label="Firma (opcjonalnie)"
                    name="company"
                    placeholder="Nazwa firmy"
                    value={form.company}
                    onChange={handleChange}
                  />

                  <Input
                    label="Hasło"
                    type="password"
                    name="password"
                    placeholder="Minimum 8 znaków"
                    value={form.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                  />

                  <Input
                    label="Potwierdź hasło"
                    type="password"
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                  />
                </div>
              )}

              {/* Step 3: RPK Data (conditional) */}
              {step === 3 && needsRpkData && (
                <div className="space-y-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Dane regulacyjne
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Jako pośrednik kredytowy musisz posiadać wpis w Rejestrze
                    Pośredników Kredytowych i Instytucji Pożyczkowych (RPKIP)
                    prowadzonym przez KNF.
                  </p>

                  <Input
                    label="Numer wpisu RPKIP"
                    name="rpkNumber"
                    placeholder="np. RPK/12345"
                    value={form.rpkNumber}
                    onChange={handleChange}
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dział rejestru RPKIP
                    </label>
                    <select
                      name="rpkDivision"
                      value={form.rpkDivision}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    >
                      <option value="">Wybierz dział...</option>
                      <option value="I">
                        Dział I - Pośrednicy kredytu hipotecznego
                      </option>
                      <option value="II">
                        Dział II - Pośrednicy kredytu konsumenckiego
                      </option>
                      <option value="I_II">Dział I i II - Oba działy</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Dział I: kredyty hipoteczne | Dział II: kredyty gotówkowe,
                      konsolidacje
                    </p>
                  </div>

                  <Input
                    label="NIP firmy/działalności (opcjonalnie)"
                    name="nip"
                    placeholder="1234567890"
                    value={form.nip}
                    onChange={handleChange}
                  />

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <Building className="w-4 h-4 inline mr-1" />
                      Twój wpis zostanie zweryfikowany w{" "}
                      <a
                        href="https://www.knf.gov.pl/podmioty/search"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        rejestrze KNF
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3/4: Compensation Model */}
              {isCompensationStep && (
                <div className="space-y-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Model wynagrodzenia
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Transparentność modelu wynagrodzenia jest ważna dla
                    budowania zaufania klientów.
                  </p>

                  <div className="space-y-3">
                    {form.roleType !== "ekspert" && (
                      <RoleCard
                        selected={form.compensationModel === "client"}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            compensationModel: "client",
                          }))
                        }
                        icon={User}
                        title="Wynagrodzenie od klienta"
                        description="Honorarium płatne bezpośrednio przez klienta za usługę doradztwa."
                      />
                    )}

                    {form.roleType !== "doradca" && (
                      <RoleCard
                        selected={form.compensationModel === "bank"}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            compensationModel: "bank",
                          }))
                        }
                        icon={Building}
                        title="Prowizja od banku"
                        description="Prowizja od instytucji finansowej za pozyskanie klienta."
                      />
                    )}

                    {form.roleType === "oba" && (
                      <RoleCard
                        selected={form.compensationModel === "mixed"}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            compensationModel: "mixed",
                          }))
                        }
                        icon={Wallet}
                        title="Model mieszany"
                        description="Połączenie honorarium od klienta i prowizji od banku, w zależności od usługi."
                      />
                    )}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-yellow-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Informacja o modelu wynagrodzenia będzie widoczna dla
                      klientów w Twoim profilu publicznym.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-3 pt-4">
                {step > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    leftIcon={<ArrowLeft className="w-4 h-4" />}
                  >
                    Wstecz
                  </Button>
                )}

                <div className="flex-1" />

                {!isLastStep && (
                  <Button
                    type="button"
                    onClick={handleNext}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                  >
                    Dalej
                  </Button>
                )}

                {isLastStep && (
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    leftIcon={<UserPlus className="w-4 h-4" />}
                  >
                    Zarejestruj się
                  </Button>
                )}
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-gray-600">
              Masz już konto?{" "}
              <Link
                to="/login"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Zaloguj się
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
